import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { validatePhone, getValidationMessage } from "@/utils/validators";
import ValidationMessage from "./ValidationMessage";
import DuplicateWarning from "./DuplicateWarning";

interface ContactStepProps {
  fullName: string;
  phone: string;
  onChangeFullName: (value: string) => void;
  onChangePhone: (value: string) => void;
  onContinue: () => void;
  phoneDuplicate?: boolean;
  onCheckDuplicate?: (field: "phone", value: string) => void;
}

const formatPhoneDisplay = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length === 0) return "";
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const ContactStep = ({
  fullName,
  phone,
  onChangeFullName,
  onChangePhone,
  onContinue,
  phoneDuplicate,
  onCheckDuplicate,
}: ContactStepProps) => {
  const [phoneDisplay, setPhoneDisplay] = useState(() => formatPhoneDisplay(phone));
  const [touched, setTouched] = useState({ name: false, phone: false });

  useEffect(() => {
    setPhoneDisplay(formatPhoneDisplay(phone));
  }, [phone]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneDisplay(e.target.value);
    setPhoneDisplay(formatted);
    const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
    onChangePhone(digits);
    if (digits.length === 11) {
      onCheckDuplicate?.("phone", digits);
    }
  };

  const isNameValid = fullName.trim().length >= 3;
  const isPhoneValid = validatePhone(phone);
  const phoneMsg = touched.phone ? getValidationMessage("phone", phone) : null;
  const canContinue = isNameValid && isPhoneValid && !phoneDuplicate;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-8"
    >
      <div className="text-center space-y-2">
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
          Como podemos te encontrar?
        </h2>
        <p className="text-muted-foreground text-base">
          Seus dados de contato para nossa reunião
        </p>
      </div>

      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="fullName" className="text-sm font-medium text-foreground">
            Nome completo
          </Label>
          <Input
            id="fullName"
            value={fullName}
            onChange={(e) => onChangeFullName(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, name: true }))}
            placeholder="Digite seu nome completo"
            className="h-14 rounded-xl border-border/50 bg-accent/30 focus:bg-background focus:border-primary transition-all text-base"
          />
          {touched.name && !isNameValid && fullName.length > 0 && (
            <ValidationMessage message="Nome deve ter pelo menos 3 caracteres" />
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="text-sm font-medium text-foreground">
            Telefone
          </Label>
          <Input
            id="phone"
            value={phoneDisplay}
            onChange={handlePhoneChange}
            onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
            placeholder="(00) 00000-0000"
            className="h-14 rounded-xl border-border/50 bg-accent/30 focus:bg-background focus:border-primary transition-all text-base"
          />
          {phoneDuplicate && <DuplicateWarning field="telefone" />}
          {phoneMsg ? (
            <ValidationMessage message={phoneMsg} />
          ) : (
            isPhoneValid && !phoneDuplicate && <ValidationMessage message={null} isValid />
          )}
        </div>
      </div>

      <Button
        onClick={onContinue}
        disabled={!canContinue}
        className="w-full h-14 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-base transition-all disabled:opacity-50"
      >
        Continuar
      </Button>
    </motion.div>
  );
};

export default ContactStep;
